import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useWallet } from '../hooks/useWallet';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import RecentActivities from './RecentActivities';
import QRCode from 'react-qr-code';
import { encodeURL } from '@solana/pay';
import { PublicKey as SolanaPublicKey } from '@solana/web3.js';
import { ConnectivityContext } from '../context/ConnectivityContext';
import { PublicKey } from '@solana/web3.js';
import { failoverConnection as rpc } from '../utils/failoverConnection';
import { TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { CONFIG } from '../config';
import { debounce } from '../utils/debounce';
import { logger } from '../utils/logger';
import { get } from '../utils/api';

// Constants
const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v';
const CETES_MINT = 'CETES7CKqqKQizuSN6iWQwmTeFRjbJR6Vw2XRKfEDR8f';
const TESLA_MINT = 'XsDoVfqeBukxuZHWhdvWHBhgEHjGNst4MLodqsJHzoB';

const CACHE_KEYS = {
  TOKEN_BALANCES: 'token_balances',
  TOKEN_BALANCES_TIMESTAMP: 'token_balances_timestamp',
  WALLET_ADDRESS: 'wallet_address'
};

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

function Dashboard() {
  const { user, error: authError } = useAuth();
  const { wallet, balances, isLoading, error: walletError } = useWallet();
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showSolTooltip, setShowSolTooltip] = useState(false);
  const [luloAccount, setLuloAccount] = useState(null);
  const [savingBalance, setSavingBalance] = useState(0);

  const [cetesBalance, setCetesBalance] = useState(0);
  const [teslaBalance, setTeslaBalance] = useState(0);
  const [savingLoading, setSavingLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  // Token configuration
  const TOKEN_CONFIGS = useMemo(() => ({
    [CETES_MINT]: { key: 'cetes', decimals: 6, setter: setCetesBalance },
    [TESLA_MINT]: { key: 'tesla', decimals: 6, setter: setTeslaBalance }
  }), []);
  const [savingError, setSavingError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showBalances, setShowBalances] = useState(true);
  const { isOnline } = useContext(ConnectivityContext);
  const navigate = useNavigate();
  
  // Calculate total savings (only include savingBalance, exclude any USD* balance)
  const totalSavings = savingBalance || 0;
  
  // Single connection instance for all RPC calls (auto-failover)
  const connection = useMemo(() => rpc, []);
  
  // Update all balances in the UI
  const updateBalances = useCallback((balances) => {
    if (!balances) return;
    
    // Safely update each balance with fallback to 0
    const safeBalances = {
      cetes: 0,
      tesla: 0,
      ...balances
    };
    
    setCetesBalance(safeBalances.cetes);
    setTeslaBalance(safeBalances.tesla);
  }, []);

  const errorRaw = authError || walletError;
  const error = errorRaw === 'Failed to create wallet. Please try again.' ? null : errorRaw;

  const shortAddress = wallet?.address
    ? `${wallet.address.slice(0,4)}...${wallet.address.slice(-4)}`
    : '...';

  const receiveUrl = wallet?.address
    ? encodeURL({ recipient: new SolanaPublicKey(wallet.address) }).toString()
    : '';

  // Fetch token balances for CETES, TESLA using per-mint RPC (rate-limit safe)
  const fetchTokenBalances = useCallback(async (force = false) => {
    const walletAddress = wallet?.address || (wallet?.publicKey ? wallet.publicKey.toBase58() : null);
    if (!walletAddress) {
      logger.log('Wallet not connected or address not available, skipping token balance fetch');
      return;
    }
    const now = Date.now();
    const cacheKey = `${CACHE_KEYS.TOKEN_BALANCES}_${walletAddress}`;
    const timestampKey = `${CACHE_KEYS.TOKEN_BALANCES_TIMESTAMP}_${walletAddress}`;

    // Check cache first if not forcing refresh
    if (!force) {
      const cachedData = localStorage.getItem(cacheKey);
      const cachedTimestamp = localStorage.getItem(timestampKey);
      if (cachedData && cachedTimestamp) {
        const age = now - parseInt(cachedTimestamp, 10);
        if (age < CACHE_DURATION) {
          const parsed = JSON.parse(cachedData);
          updateBalances(parsed);
          return;
        }
      }
    }
    setIsRefreshing(true);
    try {
      const balances = {};
      const publicKey = new PublicKey(walletAddress);

      // Sequentially fetch each token balance using the mint filter (rate-limit safe)
      for (const [mint, config] of Object.entries(TOKEN_CONFIGS)) {
        try {
          const tokenAccounts = await connection.getParsedTokenAccountsByOwner(
            publicKey,
            { programId: TOKEN_PROGRAM_ID, mint: new PublicKey(mint) }
          );
          const balance = tokenAccounts.value.length > 0
            ? tokenAccounts.value[0]?.account?.data?.parsed?.info?.tokenAmount?.uiAmount || 0
            : 0;
          balances[config.key] = balance;
        } catch (e) {
          logger.warn(`Error fetching ${config.key} balance:`, e);
          balances[config.key] = 0;
        }
      }

      // Only update cache if balances changed
      const prev = localStorage.getItem(cacheKey);
      if (!prev || prev !== JSON.stringify(balances)) {
        localStorage.setItem(cacheKey, JSON.stringify(balances));
        localStorage.setItem(timestampKey, now.toString());
      }
      updateBalances(balances);
    } catch (error) {
      logger.error('Error in fetchTokenBalances:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [wallet?.address, wallet?.publicKey, connection, updateBalances, TOKEN_CONFIGS]);

  // Fetch recent unified activities from backend
  const fetchActivities = useCallback(async () => {
    if (!wallet?.address) {
      setActivities([]);
      return;
    }
    setActivitiesLoading(true);
    try {
      const data = await get('/api/activity/recent', {
        wallet: wallet.address,
        limit: 20
      });
      setActivities(Array.isArray(data) ? data : []);
    } catch (error) {
      logger.error('Error fetching recent activities:', error);
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  }, [wallet?.address]);

  // Function to fetch Lulo savings account
  const fetchSavings = useCallback(async (force = false) => {
    if (!wallet?.address) return;
    
    // No longer using local storage for Lulo data - always fetch fresh from API
    
    try {
      setIsRefreshing(true);
      const response = await fetch(`https://api.lulo.fi/v1/account.getAccount?owner=${wallet.address}`, {
        headers: {
          'x-api-key': CONFIG.LULO_API_KEY,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch Lulo account data');
      }
      
      const data = await response.json();

      // Keep API's totalUsdValue as-is and attach only needed fields
      const filteredData = {
        totalUsdValue: data.totalUsdValue || 0,
        lusdUsdBalance: data.lusdUsdBalance || 0,
        pusdUsdBalance: data.pusdUsdBalance || 0,
        totalInterestEarned: data.totalInterestEarned || 0,
        protectedInterestEarned: data.protectedInterestEarned || 0,
        regularInterestEarned: data.regularInterestEarned || 0,
        blockTime: data.blockTime || null,
        referredBy: data.referredBy || null,
      };

      setLuloAccount(filteredData);

      // Persist snapshot to backend (best-effort, non-blocking for UI)
      try {
        await fetch('/api/lulo/upsert-account', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            walletAddress: wallet.address,
            totalUsdValue: filteredData.totalUsdValue,
            lusdUsdBalance: filteredData.lusdUsdBalance,
            pusdUsdBalance: filteredData.pusdUsdBalance,
            totalInterestEarned: filteredData.totalInterestEarned,
            protectedInterestEarned: filteredData.protectedInterestEarned,
            regularInterestEarned: filteredData.regularInterestEarned,
            blockTime: filteredData.blockTime,
            referredBy: filteredData.referredBy,
          })
        });
      } catch (e) {
        logger.warn('Failed to persist Lulo snapshot (non-blocking):', e);
      }
      
    } catch (error) {
      logger.error('Error fetching Lulo savings:', error);
      setSavingError(error.message);
    } finally {
      setSavingLoading(false);
      setIsRefreshing(false);
    }
  }, [wallet?.address]);

  // Function to fetch investments with caching
  const fetchInvestments = useCallback(async () => {
    if (!wallet?.address) return;
    
    const now = Date.now();
    const cachedData = localStorage.getItem(CACHE_KEYS.INVESTMENTS);
    const cachedTimestamp = localStorage.getItem(CACHE_KEYS.INVESTMENTS_TIMESTAMP);
    
    // Check if we have valid cached data
    if (cachedData && cachedTimestamp) {
      const timestamp = parseInt(cachedTimestamp);
      if (now - timestamp < CACHE_DURATION) {
        // Update state with cached data
        // ...
        return;
      }
    }
    
    try {
      // Fetch investments data
      // ...
      
      // Cache the investments data
      localStorage.setItem(CACHE_KEYS.INVESTMENTS, JSON.stringify(data));
      localStorage.setItem(CACHE_KEYS.INVESTMENTS_TIMESTAMP, now.toString());
      
      // Update state with fetched data
      // ...
    } catch (err) {
      logger.error('Investments fetch error:', err);
    }
  }, [wallet?.address]);

  // Prices state
  const [tokenPrices, setTokenPrices] = useState({ CETES: 1, TESLA: 1 });

  // Fetch live prices for CETES, TESLA using Jupiter
  const fetchTokenPrices = useCallback(async () => {
    try {
      const mints = [CETES_MINT, TESLA_MINT];
      const url = `https://price.jup.ag/v4/price?ids=${mints.join(',')}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setTokenPrices({
          CETES: data.data[CETES_MINT]?.price || 1,
          TESLA: data.data[TESLA_MINT]?.price || 1
        });
      }
    } catch (e) {
      setTokenPrices({ CETES: 1, TESLA: 1 });
    }
  }, []);

  useEffect(() => {
    fetchTokenPrices();
  }, [fetchTokenPrices]);

  // Debounced refresh function
  const debouncedRefresh = useMemo(
    () => {
      const refreshFn = async (force = false) => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        try {
          await Promise.allSettled([
            fetchSavings(force),
            fetchTokenBalances(force),
            fetchActivities()
          ]);
        } finally {
          setIsRefreshing(false);
        }
      };
      return debounce(refreshFn, 1000); // 1 second debounce
    },
    [fetchSavings, fetchTokenBalances, fetchActivities, isRefreshing]
  );

  // Handle refresh of all data
  const handleRefresh = useCallback(() => {
    if (isRefreshing) return;
    debouncedRefresh(true);
  }, [debouncedRefresh, isRefreshing]);

  // Add a ref to track last forced refresh time
  const lastForcedRefreshRef = React.useRef(0);
  const FORCED_REFRESH_COOLDOWN = 10000; // 10 seconds

  // Initial data load and refresh on wallet/network changes
  useEffect(() => {
    let refreshInterval;
    let walletCheckInterval;
    let isMounted = true;

    // Check for wallet connection
    const checkWalletConnection = () => {
      if (wallet?.address || wallet?.publicKey) {
        return true;
      }
      return false;
    };

    // Function to force fetch fresh data (bypassing cache)
    const forceRefreshData = async () => {
      if (!isMounted) return;
      // Prevent rapid repeated forced refreshes
      const now = Date.now();
      if (now - lastForcedRefreshRef.current < FORCED_REFRESH_COOLDOWN) {
        logger.log('Forced refresh skipped due to cooldown');
        return;
      }
      if (isRefreshing) {
        logger.log('Forced refresh skipped: already refreshing');
        return;
      }
      lastForcedRefreshRef.current = now;
      try {
        logger.log('Performing forced refresh of all data');
        await Promise.allSettled([
          fetchSavings(true),
          fetchTokenBalances(true),
          fetchActivities()
        ]);
      } catch (error) {
        logger.error('Error during forced refresh:', error);
      }
    };

    // Initial load with retry logic
    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 1000; // 1 second

    const loadData = async (force = false) => {
      if (!isMounted) return;
      if (checkWalletConnection()) {
        try {
          // On first load, force fetch fresh data
          if (force) {
            await forceRefreshData();
          } else {
            await debouncedRefresh();
          }
          // Set up the 30-minute refresh interval
          if (!refreshInterval) {
            refreshInterval = setInterval(forceRefreshData, 30 * 60 * 1000); // 30 minutes
          }
          // Clear walletCheckInterval after first successful refresh
          if (walletCheckInterval) {
            clearInterval(walletCheckInterval);
            walletCheckInterval = null;
          }
        } catch (error) {
          logger.error('Error in loadData:', error);
        }
      } else if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(() => loadData(force), retryDelay);
      } else if (isMounted) {
        logger.log('Max retries reached, wallet not connected');
      }
    };

    // Handle focus and visibility changes
    const handleFocus = () => {
      if (document.visibilityState === 'visible' && isMounted) {
        logger.log('Window gained focus, refreshing data');
        loadData(true); // Force refresh when window regains focus
      }
    };

    // Handle network status changes
    const handleOnline = () => {
      if (isMounted) {
        logger.log('Network is back online, refreshing data');
        loadData(true); // Force refresh when network comes back online
      }
    };

    // Set up event listeners
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);
    window.addEventListener('online', handleOnline);

    // Initial load with forced refresh for first login
    loadData(true);

    // Set up an interval to check for wallet connection
    walletCheckInterval = setInterval(() => {
      if (checkWalletConnection() && isMounted) {
        logger.log('Wallet connected, loading data');
        loadData(true); // Force refresh when wallet connects
        clearInterval(walletCheckInterval);
        walletCheckInterval = null;
      }
    }, 2000);

    // Cleanup function
    return () => {
      isMounted = false;
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
      window.removeEventListener('online', handleOnline);
      
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
      
      if (walletCheckInterval) {
        clearInterval(walletCheckInterval);
      }
    };
  }, [wallet?.address, wallet?.publicKey, handleRefresh, debouncedRefresh, fetchSavings, fetchTokenBalances, fetchActivities]);

// ...
  // Format wallet address
  const formatAddress = (address) => {
    if (!address) return 'Connecting...';
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="dashboard-title">Welcome back, {user?.name || 'User'}</h1>
      </div>
      
      <div className="wallet-section">
        <h2 className="section-title">Solana Wallet</h2>
        <div className="wallet-address-container">
          <div className="wallet-address">
            {formatAddress(wallet?.address)}
          </div>
          <div className="wallet-actions">
            <button 
              className="icon-button" 
              onClick={() => wallet?.address && navigator.clipboard.writeText(wallet.address)}
              title="Copy address"
            >
              📋
            </button>
            <button 
              className="icon-button" 
              onClick={() => setShowBalances(!showBalances)}
              title={showBalances ? 'Hide balances' : 'Show balances'}
            >
              {showBalances ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
        </div>
        
        <div className="balance-display">
          <div className="balance-item" style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '16px' }}>
            <span className="balance-label" style={{ fontWeight: 500 }}>USDC</span>
            <span className="balance-amount" style={{ fontWeight: 500, color: '#1F2937' }}>
              {showBalances 
                ? (balances?.USDC || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : '•••••••'}
            </span>
          </div>
          <div className="balance-item sol-balance-item" style={{position: 'relative', display: 'flex', alignItems: 'center', gap: '2.5rem'}}>
            <span className="balance-label" style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
              SOL
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <span
                  className="sol-info-icon"
                  tabIndex={0}
                  aria-label="SOL info"
                  style={{ display: 'inline-flex', cursor: 'pointer' }}
                  onClick={() => setShowSolTooltip(!showSolTooltip)}
                >
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" style={{marginLeft: '4px', verticalAlign: 'middle'}}>
                  <circle cx="10" cy="10" r="10" fill="#fbbf24"/>
                  <text x="50%" y="55%" textAnchor="middle" fill="#fff" fontSize="12" fontFamily="Arial" dy=".3em">i</text>
                </svg>
                </span>
                {showSolTooltip && (
                  <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#1f2937',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    zIndex: 10,
                    marginBottom: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                  }}>
                    SOL is for transaction fees, have at least 0.01 SOL
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: '6px solid #1f2937'
                    }}></div>
                  </div>
                )}
              </div>
            </span>
            <span
              className="balance-amount"
              style={{
                color: (parseFloat(balances?.SOL) < 0.01 ? '#f59e42' : '#1f2937'),
                fontWeight: 600,
                fontSize: '1.5rem',
                fontFeatureSettings: 'tnum',
                fontVariantNumeric: 'tabular-nums',
                marginLeft: 0,
                cursor: 'default'
              }}
            >
              {showBalances
                ? `${(balances?.SOL ?? '0.0000')} SOL`
                : '•••••••'}
            </span>

          </div>
        </div>
      </div>
      
      <div className="savings-section">
        <h2 className="section-title">Savings</h2>
        
        <div className="savings-pools">
          <div className="savings-pool" style={{ width: '100%' }}>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '16px',
              width: '100%',
              padding: '12px 0',
              borderBottom: '1px solid #e2e8f0'
            }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>Total Value</div>
                <div style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                  {showBalances 
                    ? `$${(luloAccount?.totalUsdValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}`
                    : '•••••••'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>Regular</div>
                <div style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                  {showBalances 
                    ? `$${(luloAccount?.lusdUsdBalance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}`
                    : '•••••••'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>Protected</div>
                <div style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                  {showBalances 
                    ? `$${(luloAccount?.pusdUsdBalance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}`
                    : '•••••••'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '4px' }}>Interest</div>
                <div style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                  {showBalances 
                    ? `$${(luloAccount?.totalInterestEarned || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 })}`
                    : '•••••••'}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div className="investments-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h2 className="section-title">Investments</h2>
          <button 
            onClick={() => navigate('/investment-portfolio')} 
            style={{
              background: 'none',
              border: 'none',
              color: '#3B82F6',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 8px',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(59, 130, 246, 0.05)'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            See All
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '2px' }}>
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        <div className="investments-pools" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {cetesBalance !== null && (
            <div className="investments-pool" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '8px 0' }}>
              <span style={{ fontWeight: '500', color: '#1F2937' }}>CETES</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: '500', color: '#1F2937' }}>{showBalances ? cetesBalance.toFixed(2) : '••••••'}</div>
                <div style={{ fontSize: '14px', color: '#6B7280' }}>${showBalances ? (cetesBalance * tokenPrices.CETES).toFixed(2) : '••••••'}</div>
              </div>
            </div>
          )}
          {teslaBalance !== null && (
            <div className="investments-pool" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '8px 0' }}>
              <span style={{ fontWeight: '500', color: '#1F2937' }}>TESLA</span>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: '500', color: '#1F2937' }}>{showBalances ? teslaBalance.toFixed(2) : '••••••'}</div>
                <div style={{ fontSize: '14px', color: '#6B7280' }}>${showBalances ? (teslaBalance * tokenPrices.TESLA).toFixed(2) : '••••••'}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="quick-actions-section">
        <h2 className="section-title">Quick Actions</h2>
        <div className="action-buttons">
          <button 
            className="action-button"
            onClick={() => navigate('/send')}
          >
            Send
          </button>
          <button 
            className="action-button"
            onClick={() => setShowReceiveModal(true)}
          >
            Receive
          </button>
          <button 
            className="action-button"
            onClick={() => window.location.href = '/pools'}
          >
            Save
          </button>
        </div>
      </div>

      <div className="transaction-section">
        <div className="transaction-list">
          <RecentActivities activities={activities} loading={activitiesLoading} maxItems={10} />
        </div>
      </div>

      {showReceiveModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Receive Funds</h3>
            {wallet?.address && <QRCode value={receiveUrl} size={200} />}
            <p className="wallet-address-text">{wallet?.address}</p>
            <div className="modal-actions">
              <button 
                className="modal-button" 
                onClick={() => navigator.clipboard.writeText(wallet?.address || '')}
              >
                Copy Address
              </button>
              <button 
                className="modal-button secondary"
                onClick={() => setShowReceiveModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .dashboard-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem 1rem;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        
        .dashboard-header {
          margin-bottom: 2rem;
        }
        
        .dashboard-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0;
        }
        
        .section-title {
          font-size: 1.25rem !important;
          font-weight: 700 !important;
          color: #000000 !important; /* Pure black for maximum contrast */
          margin: 0 0 1.25rem 0 !important;
          letter-spacing: -0.01em !important;
          text-rendering: optimizeLegibility !important;
          -webkit-font-smoothing: antialiased !important;
          -moz-osx-font-smoothing: grayscale !important;
          opacity: 1 !important;
          text-shadow: none !important;
        }
        
        .wallet-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }
        
        .wallet-address-container {
          display: flex;
          align-items: center;
          background: #f8f9fa;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          margin: 1rem 0;
        }
        
        .wallet-address {
          flex: 1;
          font-family: 'Roboto Mono', monospace;
          font-size: 0.9rem;
          color: #333;
          word-break: break-all;
        }
        
        .wallet-actions {
          display: flex;
          gap: 0.5rem;
          margin-left: 0.5rem;
        }
        
        .icon-button {
          background: none;
          border: none;
          font-size: 1.1rem;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          transition: background 0.2s;
        }
        
        .icon-button:hover {
          background: rgba(0, 0, 0, 0.05);
        }
        
        .balance-display {
          display: flex;
          gap: 2rem;
          margin-top: 1.5rem;
        }
        
        .balance-item {
          flex: 1;
        }
        
        .balance-label {
          display: block;
          font-size: 0.875rem;
          color: #6b7280;
          margin-bottom: 0.25rem;
        }
        
        .balance-amount {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1f2937;
          font-feature-settings: 'tnum';
          font-variant-numeric: tabular-nums;
        }
        
        .savings-section,
        .investments-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }
        
        .savings-total {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1rem;
          margin: 1rem 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .savings-pools {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        
        .savings-pool {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f0f0f0;
        }
        
        .savings-pool:last-child {
          border-bottom: none;
        }
        
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        
        .view-all {
          background: none;
          border: none;
          color: #3b82f6;
          font-weight: 500;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          transition: background 0.2s;
        }
        
        .view-all:hover {
          background: rgba(59, 130, 246, 0.1);
        }
        
        .investments-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
        }
        
        .investment-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 1.25rem;
          transition: transform 0.2s, box-shadow 0.2s;
          cursor: pointer;
        }
        
        .investment-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }
        
        .investment-header {
          display: flex;
          align-items: center;
          margin-bottom: 0.75rem;
        }
        
        .investment-icon {
          font-size: 1.5rem;
          margin-right: 0.75rem;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: rgba(59, 130, 246, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .investment-name {
          font-weight: 500;
          color: #1f2937;
        }
        
        .investment-balance {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 0.5rem 0;
          color: #1f2937;
        }
        
        .investment-details {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.5rem;
        }
        
        .investment-apy {
          color: #10b981;
          font-weight: 500;
        }
        
        .investment-price {
          color: #4b5563;
        }
        
        .pool-name {
          color: #4b5563;
          font-weight: 500;
        }
        
        .pool-balance {
          font-weight: 600;
          color: #1f2937;
          font-feature-settings: 'tnum';
          font-variant-numeric: tabular-nums;
        }
        
        .quick-actions-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }
        
        .action-buttons {
          display: flex;
          gap: 1rem;
          margin-top: 1rem;
        }
        
        .action-button {
          flex: 1;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1rem;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }
        
        .action-button:hover {
          background: #2563eb;
        }
        
        .transaction-section {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
        }
        
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          width: 90%;
          max-width: 400px;
          text-align: center;
        }
        
        .modal-actions {
          display: flex;
          gap: 1rem;
          margin-top: 1.5rem;
        }
        
        .modal-button {
          flex: 1;
          padding: 0.75rem;
          border: none;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
          transition: opacity 0.2s;
        }
        
        .modal-button {
          background: #3b82f6;
          color: white;
        }
        
        .modal-button.secondary {
          background: #e5e7eb;
          color: #4b5563;
        }
        
        .modal-button:hover {
          opacity: 0.9;
        }
        
        .wallet-address-text {
          font-family: 'Roboto Mono', monospace;
          font-size: 0.875rem;
          background: #f8f9fa;
          padding: 0.75rem;
          border-radius: 8px;
          margin: 1rem 0;
          word-break: break-all;
        }
        
        @media (max-width: 600px) {
          .balance-display {
            flex-direction: column;
            gap: 1rem;
          }
          
          .action-buttons {
            flex-direction: column;
          }
          
          .savings-pool > div {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Dashboard;